<template>
  <b-modal
    v-model="show"
    :title="pedido ? `Pagos del pedido de ${pedido.cliente.nombre || pedido.cliente.empresa || '—'}` : 'Pagos'"
    hide-footer
    centered
  >
    <div v-if="pedido">
      <!-- Resumen -->
      <div class="esc-pagos-resumen mb-1">
        <div>
          <small class="text-muted d-block">Total</small>
          <span class="font-weight-bolder">{{ pedido.simbolo }}{{ pedido.total }}</span>
        </div>
        <div>
          <small class="text-muted d-block">Pagado</small>
          <span class="font-weight-bolder text-success">{{ pedido.simbolo }}{{ totalPagado }}</span>
        </div>
        <div>
          <small class="text-muted d-block">Pendiente</small>
          <span
            class="font-weight-bolder"
            :class="pendientePago > 0 ? 'text-danger' : 'text-success'"
          >{{ pedido.simbolo }}{{ pendientePago }}</span>
        </div>
      </div>

      <!-- Historial de pagos -->
      <div v-if="cargando" class="text-center my-2">
        <b-spinner small class="align-middle mr-50" /> Cargando…
      </div>
      <div v-else-if="!pagos.length" class="text-center text-muted my-2">
        Sin pagos registrados
      </div>
      <div v-else class="esc-pagos-list mb-1">
        <div v-for="p in pagos" :key="p.id" class="esc-pago-row">
          <div>
            <span class="font-weight-bold">{{ pedido.simbolo }}{{ p.importe }}</span>
            <small class="text-muted ml-50">{{ formatFecha(p.fecha) }}</small>
            <b-badge v-if="p.metodo" variant="light-secondary" class="ml-50">{{ p.metodo }}</b-badge>
            <small v-if="p.nota" class="text-muted d-block">{{ p.nota }}</small>
          </div>
          <b-button size="sm" variant="flat-danger" class="btn-icon" @click="eliminarPago(p)">
            <i class="fa-solid fa-trash-can"></i>
          </b-button>
        </div>
      </div>

      <hr />

      <!-- Registrar pago -->
      <b-row>
        <b-col cols="6">
          <b-form-group label="Importe" label-class="small font-weight-bold">
            <b-form-input
              v-model="nuevoPago.importe"
              type="number"
              min="0"
              step="0.01"
              :placeholder="String(pendientePago)"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="Fecha" label-class="small font-weight-bold">
            <b-form-input v-model="nuevoPago.fecha" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="Método" label-class="small font-weight-bold">
            <b-form-select v-model="nuevoPago.metodo" :options="metodosPago" />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group label="Nota" label-class="small font-weight-bold">
            <b-form-input v-model="nuevoPago.nota" placeholder="Opcional" />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="text-right">
        <b-button
          variant="primary"
          :disabled="guardando || !(Number(nuevoPago.importe) > 0)"
          @click="anadirPago"
        >
          <b-spinner v-if="guardando" small class="mr-50" />
          <i v-else class="fa-solid fa-plus mr-50"></i> Registrar pago
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BBadge, BButton, BRow, BCol, BFormGroup, BFormInput, BFormSelect,
} from 'bootstrap-vue'
import axios from '@axios'

const pagoVacio = () => ({
  importe: '', fecha: new Date().toISOString().slice(0, 10), metodo: 'Transferencia', nota: '',
})

export default {
  components: {
    BModal, BSpinner, BBadge, BButton, BRow, BCol, BFormGroup, BFormInput, BFormSelect,
  },
  data() {
    return {
      show: false,
      pedido: null,
      pagos: [],
      cargando: false,
      guardando: false,
      nuevoPago: pagoVacio(),
      metodosPago: ['Transferencia', 'Efectivo', 'Tarjeta', 'PayPal', 'Otro'],
    }
  },
  computed: {
    totalPagado() {
      return Math.round(this.pagos.reduce((acc, p) => acc + (Number(p.importe) || 0), 0) * 100) / 100
    },
    pendientePago() {
      if (!this.pedido) return 0
      return Math.max(0, Math.round(((Number(this.pedido.total) || 0) - this.totalPagado) * 100) / 100)
    },
  },
  methods: {
    // API pública: el padre llama a this.$refs.x.abrir(pedido)
    abrir(pedido) {
      this.pedido = pedido
      this.pagos = []
      this.nuevoPago = pagoVacio()
      this.show = true
      this.cargarPagos()
    },
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Pagos',
        variant,
        solidColor: true,
      })
    },
    formatFecha(f) {
      if (!f) return ''
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-ES')
    },
    cargarPagos() {
      this.cargando = true
      axios
        .get(`/escaner/pedidos/${this.pedido.id}/pagos`)
        .then(({ data }) => {
          this.pagos = data || []
          // Sincroniza el pedido del padre (badge de la tabla) con la suma real
          this.$set(this.pedido, 'pagado', this.totalPagado)
        })
        .catch(() => { this.notificar('No se pudieron cargar los pagos', 'danger') })
        .finally(() => { this.cargando = false })
    },
    anadirPago() {
      this.guardando = true
      axios
        .post(`/escaner/pedidos/${this.pedido.id}/pagos`, this.nuevoPago)
        .then(() => {
          this.nuevoPago = pagoVacio()
          this.cargarPagos()
        })
        .catch(() => { this.notificar('No se pudo guardar el pago', 'danger') })
        .finally(() => { this.guardando = false })
    },
    eliminarPago(pago) {
      this.$bvModal
        .msgBoxConfirm(`Se eliminará el pago de ${this.pedido.simbolo}${pago.importe}.`, {
          title: 'Eliminar pago',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          centered: true,
        })
        .then((ok) => {
          if (!ok) return
          axios
            .delete(`/escaner/pedidos/${this.pedido.id}/pagos/${pago.id}`)
            .then(() => { this.cargarPagos() })
            .catch(() => { this.notificar('No se pudo eliminar el pago', 'danger') })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.esc-pagos-resumen {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
}
.esc-pagos-list {
  max-height: 220px;
  overflow-y: auto;
}
.esc-pago-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.25rem;
  border-bottom: 1px solid #ebe9f1;

  &:last-child { border-bottom: none; }
}
</style>
